import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { IngestionResponse, TaskStatus } from '@/lib/api'

export type WorkflowNode =
  | 'IngestNode'
  | 'CardifyNode'
  | 'EmbedNode'
  | 'CanonicalizeNode'
  | 'LinkNode'
  | 'UpdateStatsNode'
  | 'ReviewQueueNode'
  | 'SessionManagerNode'

export interface NodeProgress {
  node: WorkflowNode
  status: 'pending' | 'running' | 'completed' | 'failed'
  output?: Record<string, unknown>
}

interface IngestState {
  // Editor content (persisted)
  content: string
  setContent: (content: string) => void

  // Metadata (persisted)
  title: string
  setTitle: (title: string) => void
  domainHint: string | null
  setDomainHint: (domain: string | null) => void
  tags: string[]
  setTags: (tags: string[]) => void
  projectTag: string | null
  setProjectTag: (tag: string | null) => void
  filename: string | null
  setFilename: (filename: string | null) => void

  // Workflow status (not persisted)
  isProcessing: boolean
  currentTask: IngestionResponse | null
  taskStatus: TaskStatus | null
  nodeProgress: NodeProgress[]
  error: string | null

  // Actions
  startProcessing: (task: IngestionResponse) => void
  updateTaskStatus: (status: TaskStatus) => void
  updateNodeProgress: (node: WorkflowNode, status: NodeProgress['status'], output?: Record<string, unknown>) => void
  setError: (error: string | null) => void
  reset: () => void
}

const WORKFLOW_NODES: WorkflowNode[] = [
  'IngestNode',
  'CardifyNode',
  'EmbedNode',
  'CanonicalizeNode',
  'LinkNode',
  'UpdateStatsNode',
  'ReviewQueueNode',
  'SessionManagerNode',
]

const initialNodeProgress: NodeProgress[] = WORKFLOW_NODES.map((node) => ({
  node,
  status: 'pending',
}))

export const useIngestStore = create<IngestState>()(
  persist(
    (set) => ({
      content: '',
      setContent: (content) => set({ content }),

      title: '',
      setTitle: (title) => set({ title }),
      domainHint: null,
      setDomainHint: (domainHint) => set({ domainHint }),
      tags: [],
      setTags: (tags) => set({ tags }),
      projectTag: null,
      setProjectTag: (projectTag) => set({ projectTag }),
      filename: null,
      setFilename: (filename) => set({ filename }),

      isProcessing: false,
      currentTask: null,
      taskStatus: null,
      nodeProgress: initialNodeProgress,
      error: null,

      startProcessing: (task) =>
        set({
          isProcessing: true,
          currentTask: task,
          taskStatus: null,
          nodeProgress: initialNodeProgress.map((n, i) => ({
            ...n,
            status: i === 0 ? 'running' : 'pending',
          })),
          error: null,
        }),

      updateTaskStatus: (status) =>
        set({
          taskStatus: status,
          isProcessing: status.status !== 'COMPLETED' && status.status !== 'FAILED',
        }),

      updateNodeProgress: (node, status, output) =>
        set((state) => {
          const nodeIndex = WORKFLOW_NODES.indexOf(node)
          const newProgress = [...state.nodeProgress]
          newProgress[nodeIndex] = { node, status, output }

          // If node completed, start next node
          if (status === 'completed' && nodeIndex < WORKFLOW_NODES.length - 1) {
            newProgress[nodeIndex + 1] = {
              ...newProgress[nodeIndex + 1],
              status: 'running',
            }
          }

          // Check if all nodes completed
          const allCompleted = newProgress.every((n) => n.status === 'completed')
          const anyFailed = newProgress.some((n) => n.status === 'failed')

          return {
            nodeProgress: newProgress,
            isProcessing: !allCompleted && !anyFailed,
          }
        }),

      setError: (error) => set({ error, isProcessing: false }),

      reset: () =>
        set({
          content: '',
          title: '',
          domainHint: null,
          tags: [],
          projectTag: null,
          filename: null,
          isProcessing: false,
          currentTask: null,
          taskStatus: null,
          nodeProgress: initialNodeProgress,
          error: null,
        }),
    }),
    {
      name: 'ingest-store',
      // Only persist content and metadata, not workflow state
      partialize: (state) => ({
        content: state.content,
        title: state.title,
        domainHint: state.domainHint,
        tags: state.tags,
        projectTag: state.projectTag,
        filename: state.filename,
      }),
    }
  )
)
